/**
 * Date — tutto in stringhe ISO `YYYY-MM-DD`, senza oggetti `Date` nel dominio.
 *
 * Una scadenza è un giorno di calendario, non un istante: passare da `Date`
 * significa portarsi dietro un fuso orario che sposta le fatture di un giorno
 * a cavallo di mezzanotte, e in un previsionale mensile quel giorno può
 * spostare un incasso da settembre a ottobre.
 */
const RE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Ritorna un `boolean`, non un type predicate: `IsoData` è un alias di `string`,
 * quindi restringere su `v is IsoData` lascerebbe `never` nel ramo negativo e
 * farebbe fallire il codice che dopo il controllo continua a trattare il valore
 * come stringa. L'alias serve a documentare, non a restringere.
 */
export function isIsoData(v) {
    if (typeof v !== 'string')
        return false;
    const m = RE_ISO.exec(v);
    if (!m)
        return false;
    const [, a, me, g] = m;
    const d = new Date(Date.UTC(+a, +me - 1, +g));
    return d.getUTCFullYear() === +a && d.getUTCMonth() === +me - 1 && d.getUTCDate() === +g;
}
function assertIso(v) {
    if (!isIsoData(v))
        throw new Error(`Data non valida: "${v}"`);
}
function utc(iso) {
    assertIso(iso);
    const [a, m, g] = iso.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, g));
}
function iso(d) {
    return d.toISOString().slice(0, 10);
}
export function oggi(riferimento = new Date()) {
    return `${riferimento.getFullYear()}-${String(riferimento.getMonth() + 1).padStart(2, '0')}-${String(riferimento.getDate()).padStart(2, '0')}`;
}
export function aggiungiGiorni(data, giorni) {
    const d = utc(data);
    d.setUTCDate(d.getUTCDate() + giorni);
    return iso(d);
}
/** Somma mesi mantenendo il giorno, con clamp a fine mese (31 gen + 1 = 28 feb). */
export function aggiungiMesi(data, mesi) {
    const d = utc(data);
    const giorno = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + mesi);
    const ultimo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(giorno, ultimo));
    return iso(d);
}
export function giorniTra(da, a) {
    return Math.round((utc(a).getTime() - utc(da).getTime()) / 86_400_000);
}
export function meseDi(data) {
    assertIso(data);
    return data.slice(0, 7);
}
export function primoGiornoDelMese(mese) {
    return `${mese}-01`;
}
export function ultimoGiornoDelMese(mese) {
    const [a, m] = mese.split('-').map(Number);
    return iso(new Date(Date.UTC(a, m, 0)));
}
export function meseSuccessivo(mese, passi = 1) {
    return meseDi(aggiungiMesi(primoGiornoDelMese(mese), passi));
}
/** Elenco di `quanti` mesi consecutivi a partire da `da` incluso. */
export function elencoMesi(da, quanti) {
    return Array.from({ length: quanti }, (_, i) => meseSuccessivo(da, i));
}
/** Lunedì della settimana ISO che contiene la data. */
export function lunediDi(data) {
    const d = utc(data);
    const dow = (d.getUTCDay() + 6) % 7; // lunedì = 0
    d.setUTCDate(d.getUTCDate() - dow);
    return iso(d);
}
/** Chiave settimana ISO 8601, `YYYY-Www`. */
export function settimanaDi(data) {
    const d = utc(data);
    const dow = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - dow + 3); // giovedì della settimana ISO
    const anno = d.getUTCFullYear();
    const primoGiovedi = new Date(Date.UTC(anno, 0, 4));
    const dowPrimo = (primoGiovedi.getUTCDay() + 6) % 7;
    primoGiovedi.setUTCDate(primoGiovedi.getUTCDate() - dowPrimo + 3);
    const settimana = 1 + Math.round((d.getTime() - primoGiovedi.getTime()) / (7 * 86_400_000));
    return `${anno}-W${String(settimana).padStart(2, '0')}`;
}
/** Elenco di `quante` settimane consecutive a partire dal lunedì di `da`. */
export function elencoSettimane(da, quante) {
    const primo = lunediDi(da);
    return Array.from({ length: quante }, (_, i) => {
        const inizio = aggiungiGiorni(primo, i * 7);
        return { chiave: settimanaDi(inizio), inizio, fine: aggiungiGiorni(inizio, 6) };
    });
}
const MESI_IT = [
    'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
    'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];
/** `2026-09` → `"set 2026"` */
export function etichettaMese(mese, opzioni) {
    const [a, m] = mese.split('-').map(Number);
    const nome = MESI_IT[m - 1];
    return opzioni?.lungo ? `${nome} ${a}` : `${nome.slice(0, 3)} ${a}`;
}
export function formattaData(data, formato = 'gg/mm/aaaa') {
    assertIso(data);
    const [a, m, g] = data.split('-');
    switch (formato) {
        case 'gg/mm/aaaa':
            return `${g}/${m}/${a}`;
        case 'gg-mm-aaaa':
            return `${g}-${m}-${a}`;
        case 'aaaa-mm-gg':
            return data;
    }
}
/** Converte `gg/mm/aaaa` in ISO. Ritorna null se non è una data valida. */
export function isoDaItaliano(testo) {
    const m = /^\s*(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\s*$/.exec(testo);
    if (!m)
        return null;
    const [, g, me, a] = m;
    const anno = a.length === 2 ? `20${a}` : a;
    const candidato = `${anno}-${me.padStart(2, '0')}-${g.padStart(2, '0')}`;
    return isIsoData(candidato) ? candidato : null;
}
//# sourceMappingURL=date.js.map